"use client"

import { useMemo, type ReactNode } from "react"
import { toast as sonnerToast } from "sonner"
import { Toaster } from "@/components/ui/sonner"

export type ToastVariant = "default" | "success" | "warning" | "danger"

export type ToastItem = {
  id: string
  title: ReactNode
  description?: ReactNode
  variant?: ToastVariant
  durationMs?: number
}

type ToastContextValue = {
  toast: (input: Omit<ToastItem, "id">) => string
  dismiss: (id: string) => void
}

const VARIANT_TO_SONNER: Record<ToastVariant, "success" | "warning" | "error" | "info" | "message"> = {
  default: "message",
  success: "success",
  warning: "warning",
  danger: "error",
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <Toaster />
    </>
  )
}

export function useToast(): ToastContextValue {
  return useMemo<ToastContextValue>(
    () => ({
      toast: ({ title, description, variant = "default", durationMs }) => {
        const kind = VARIANT_TO_SONNER[variant]
        const opts: { description?: ReactNode; duration?: number } = {}
        if (description !== undefined) opts.description = description
        if (durationMs !== undefined) opts.duration = durationMs
        const id = kind === "message" ? sonnerToast(title, opts) : sonnerToast[kind](title, opts)
        return String(id)
      },
      dismiss: (id: string) => {
        sonnerToast.dismiss(id)
      },
    }),
    [],
  )
}
